/**
 * Stakeholder Create Route
 *
 * Create a new stakeholder using StakeholderForm component
 */

import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useApolloClient } from '@apollo/client/react';
import { StakeholderForm } from '../../components/stakeholders';
import { createStakeholder, type CreateStakeholderInput, ApiError } from '../../lib/apiClient';
import type { CreateStakeholderFormData } from '../../components/forms';
import './new.css';

export const Route = createFileRoute('/stakeholders/new')({
  component: StakeholderCreatePage,
});

function StakeholderCreatePage(): JSX.Element {
  const BannerElement = 'forge-banner' as React.ElementType;
  const IconElement = 'forge-icon' as React.ElementType;
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (formData: CreateStakeholderFormData): Promise<void> => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Build create input
      const input: CreateStakeholderInput = {
        name: formData.name,
        stakeholderType: formData.stakeholderType,
        description: formData.description || undefined,
        contactEmail: formData.contactEmail || undefined,
        organization: formData.organization || undefined,
      };

      const newStakeholder = await createStakeholder(input);

      // Refetch queries to update cache
      await apolloClient.refetchQueries({
        include: ['ListStakeholders'],
      });

      // Navigate to the new stakeholder detail page
      navigate({ to: `/stakeholders/${newStakeholder.entityId}` as string });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    navigate({ to: '/stakeholders', search: { page: 1, pageSize: 25 } });
  };

  return (
    <div className="stakeholder-create-page">
      <div className="page-header page-header--stacked">
        <h1 className="forge-typography--headline4">Create Stakeholder</h1>
        <p className="forge-typography--body2 page-header-subtitle">
          Add a new stakeholder to the organization
        </p>
      </div>

      {error && (
        <BannerElement theme="error" dismissed={false} className="error-banner" role="alert">
          <div className="banner-content">
            <IconElement name="error" aria-hidden="true" />
            <span>{error}</span>
          </div>
        </BannerElement>
      )}

      <StakeholderForm
        mode="create"
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
